import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';

const EMPTY_MODEL = {
  title: '',
  description: '',
  category: '',
  print_time: '',
  filament: 'PLA',
  difficulty: 'Iniciante',
  supports_needed: false,
  assembly_required: null,
  printer_compatibility: 'Todas Impressoras FDM',
  material_usage: '',
  viral_score: 50,
  etsy_potential: 50,
  suggested_price: '',
  etsy_title: '',
  etsy_tags: '',
  tiktok_hooks: '',
  video_angles: '',
  creator_name: '',
  creator_platform: '',
  download_link: '',
  is_premium: false,
  is_featured: false
};

export function ModelFormDialog({ open, onClose, onSuccess, model = null }) {
  const [formData, setFormData] = useState(EMPTY_MODEL);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const isEdit = !!model;

  useEffect(() => {
    if (open) {
      loadCategories();
    }
  }, [open]);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
      if (!model && res.data.length > 0) {
        setFormData(prev => ({ ...prev, category: prev.category || res.data[0].id }));
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  useEffect(() => {
    if (model) {
      setFormData({
        ...model,
        etsy_tags: Array.isArray(model.etsy_tags) ? model.etsy_tags.join(', ') : '',
        tiktok_hooks: Array.isArray(model.tiktok_hooks) ? model.tiktok_hooks.join('\n') : '',
        video_angles: Array.isArray(model.video_angles) ? model.video_angles.join('\n') : ''
      });
    } else {
      setFormData(EMPTY_MODEL);
    }
    setImageFile(null);
  }, [model, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        viral_score: parseInt(formData.viral_score) || 0,
        etsy_potential: parseInt(formData.etsy_potential) || 0,
        etsy_tags: formData.etsy_tags.split(',').map(t => t.trim()).filter(Boolean),
        tiktok_hooks: formData.tiktok_hooks.split('\n').map(t => t.trim()).filter(Boolean),
        video_angles: formData.video_angles.split('\n').map(t => t.trim()).filter(Boolean)
      };

      let modelId;
      if (isEdit) {
        await axios.put(`${API}/models/${model.id}`, payload, { withCredentials: true });
        modelId = model.id;
        toast.success('Modelo atualizado com sucesso');
      } else {
        const res = await axios.post(`${API}/models`, payload, { withCredentials: true });
        modelId = res.data.id;
        toast.success('Modelo criado com sucesso');
      }

      if (imageFile && modelId) {
        const fd = new FormData();
        fd.append('file', imageFile);
        await axios.post(`${API}/models/${modelId}/images`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Imagem enviada');
      }

      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar modelo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            data-testid="model-form-dialog"
            className="relative w-full max-w-3xl my-8 bg-[#121212] border border-[#27272A] rounded-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-black text-white tracking-tight">
                {isEdit ? 'Edit Model' : 'Add New Model'}
              </h2>
              <button
                data-testid="close-dialog-button"
                onClick={onClose}
                className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Título" required>
                  <input
                    data-testid="model-title-input"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    required
                    className={inputClass}
                  />
                </Field>
                <Field label="Categoria" required>
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    required
                    className={inputClass}
                  >
                    {categories.length === 0 ? (
                      <option value="">Carregando...</option>
                    ) : (
                      categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                    )}
                  </select>
                </Field>
              </div>

              <Field label="Descrição" required>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  required
                  rows={3}
                  className={inputClass}
                />
              </Field>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Field label="Tempo de Impressão">
                  <input type="text" value={formData.print_time} onChange={(e) => handleChange('print_time', e.target.value)} placeholder="6 horas" className={inputClass} />
                </Field>
                <Field label="Filamento">
                  <input type="text" value={formData.filament} onChange={(e) => handleChange('filament', e.target.value)} className={inputClass} />
                </Field>
                <Field label="Dificuldade">
                  <select value={formData.difficulty} onChange={(e) => handleChange('difficulty', e.target.value)} className={inputClass}>
                    <option>Iniciante</option>
                    <option>Médio</option>
                    <option>Avançado</option>
                  </select>
                </Field>
                <Field label="Material Utilizado (g)">
                  <input type="text" value={formData.material_usage} onChange={(e) => handleChange('material_usage', e.target.value)} placeholder="35" className={inputClass} />                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Compatibilidade">
                  <input type="text" value={formData.printer_compatibility} onChange={(e) => handleChange('printer_compatibility', e.target.value)} className={inputClass} />
                </Field>
                <div className="flex items-end space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.supports_needed}
                      onChange={(e) => handleChange('supports_needed', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-white text-sm">Precisa de Suportes</span>
                  </label>
                </div>
              </div>

              <Field label="Montagem Necessária">
                <select
                  value={formData.assembly_required === null || formData.assembly_required === undefined ? '' : (formData.assembly_required ? 'sim' : 'nao')}
                  onChange={(e) => {
                    const v = e.target.value;
                    handleChange('assembly_required', v === '' ? null : v === 'sim');
                  }}
                  className={inputClass}
                >
                  <option value="">-- Não informado --</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Pontuação Viral (0-100)">
                  <input type="number" min="0" max="100" value={formData.viral_score} onChange={(e) => handleChange('viral_score', e.target.value)} className={inputClass} />
                </Field>
                <Field label="Potencial de Venda (0-100)">
                  <input type="number" min="0" max="100" value={formData.etsy_potential} onChange={(e) => handleChange('etsy_potential', e.target.value)} className={inputClass} />
                </Field>
              </div>

              <Field label="Preço Sugerido">
                <input type="text" value={formData.suggested_price} onChange={(e) => handleChange('suggested_price', e.target.value)} placeholder="R$ 50-75" className={inputClass} />
              </Field>

              <Field label="Título Marketplace">
                <input type="text" value={formData.etsy_title} onChange={(e) => handleChange('etsy_title', e.target.value)} className={inputClass} />
              </Field>

              <Field label="Tags Marketplace (separadas por vírgula)">
                <input type="text" value={formData.etsy_tags} onChange={(e) => handleChange('etsy_tags', e.target.value)} placeholder="dragão, fidget, impressão 3d" className={inputClass} />
              </Field>

              <Field label="Ganchos TikTok (um por linha)">
                <textarea value={formData.tiktok_hooks} onChange={(e) => handleChange('tiktok_hooks', e.target.value)} rows={2} className={inputClass} />
              </Field>

              <Field label="Ângulos de Vídeo (um por linha)">
                <textarea value={formData.video_angles} onChange={(e) => handleChange('video_angles', e.target.value)} rows={2} className={inputClass} />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nome do Criador" required>
                  <input type="text" value={formData.creator_name} onChange={(e) => handleChange('creator_name', e.target.value)} required className={inputClass} />
                </Field>
                <Field label="Plataforma do Criador" required>
                  <input type="text" value={formData.creator_platform} onChange={(e) => handleChange('creator_platform', e.target.value)} required placeholder="Thingiverse" className={inputClass} />
                </Field>
              </div>

              <Field label="Link de Download Original" required>
                <input type="url" value={formData.download_link} onChange={(e) => handleChange('download_link', e.target.value)} required placeholder="https://..." className={inputClass} />
              </Field>

              <Field label="Imagem de Prévia">
                <label className="flex items-center justify-center space-x-3 w-full bg-[#1A1A1A] border-2 border-dashed border-[#27272A] hover:border-[#0066FF] rounded-lg p-4 cursor-pointer transition-all">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => setImageFile(e.target.files[0])}
                    className="hidden"
                    data-testid="model-image-input"
                  />
                  <Upload className="w-5 h-5 text-[#0066FF]" />
                  <span className="text-white text-sm">{imageFile ? imageFile.name : 'Clique para enviar imagem (jpg, png, webp)'}</span>
                </label>
              </Field>

              <div className="flex items-center space-x-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_premium} onChange={(e) => handleChange('is_premium', e.target.checked)} className="w-4 h-4" />
                  <span className="text-white text-sm font-semibold">Apenas Premium</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_featured} onChange={(e) => handleChange('is_featured', e.target.checked)} className="w-4 h-4" />
                  <span className="text-white text-sm font-semibold">Destaque</span>
                </label>
              </div>

              <div className="flex items-center space-x-3 pt-4 border-t border-[#27272A]">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 bg-[#1A1A1A] border border-[#27272A] text-white rounded-lg hover:border-zinc-500 transition-colors font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="submit-model-button"
                  disabled={loading}
                  className="flex-1 px-6 py-2.5 bg-[#0066FF] text-white rounded-lg hover:bg-[#3385FF] transition-colors font-bold disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : (isEdit ? 'Atualizar Modelo' : 'Criar Modelo')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputClass = "w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-white mb-1.5">
        {label}{required && <span className="text-[#0066FF] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
